import { ScopeOfWork } from "@/api/williams/scopesOfWork";

export enum ServiceType {
  Sour = "Sour",
  Normal = "Normal",
}

export enum ContinuityType {
  CFR192 = "CFR192",
  CFR195 = "CFR195",
  Basic = "Basic",
  BasicNDE = "Basic + NDE",
}

export enum MaterialType {
  CarbonSteel = "Carbon and low-alloy steel",
  StainlessSteel = "Stainless Steel (Austenitic)",
  NotListed = "Not Listed",
}

export enum WPSCode {
  ASMESectionIX = "ASME Section IX",
  API_1104 = "API 1104",
  InServiceASMEB31_3 = "In-service for ASME B31.3",
  NotListed = "Not Listed",
  Unknown = "Unknown",
}

export enum ConstructionCode {
  CFR192_ASMEB31_8 = "CFR192/ASMEB31.8",
  CFR195_ASMEB31_4 = "CFR195/ASMEB31.4",
  CFR193_ASMEB31_3 = "CFR193/ASMEB31.3",
  ASMEB31_8_NonCFR = "ASMEB31.8/Non-CFR",
  ASMEB31_4_NonCFR = "ASMEB31.4/Non-CFR",
  ASMEB31_3_NonCFR = "ASMEB31.3/Non-CFR",
  Unknown = "Unknown",
  NonCode = "Non-Code",
  NotListed = "Not listed",
}

const WPSCodes_InService: Record<ConstructionCode, WPSCode[]> = {
  [ConstructionCode.CFR192_ASMEB31_8]: [WPSCode.API_1104],
  [ConstructionCode.CFR195_ASMEB31_4]: [WPSCode.API_1104],
  [ConstructionCode.CFR193_ASMEB31_3]: [WPSCode.InServiceASMEB31_3],
  [ConstructionCode.ASMEB31_8_NonCFR]: [WPSCode.API_1104],
  [ConstructionCode.ASMEB31_4_NonCFR]: [WPSCode.ASMESectionIX, WPSCode.API_1104],
  [ConstructionCode.ASMEB31_3_NonCFR]: [WPSCode.InServiceASMEB31_3],
  [ConstructionCode.Unknown]: [WPSCode.API_1104, WPSCode.NotListed, WPSCode.Unknown],
  [ConstructionCode.NonCode]: [WPSCode.API_1104, WPSCode.NotListed, WPSCode.Unknown],
  [ConstructionCode.NotListed]: [WPSCode.API_1104, WPSCode.NotListed, WPSCode.Unknown],
};

const WPSCodes_OutOfService: Record<ConstructionCode, WPSCode[]> = {
  [ConstructionCode.CFR192_ASMEB31_8]: [WPSCode.API_1104, WPSCode.ASMESectionIX],
  [ConstructionCode.CFR195_ASMEB31_4]: [WPSCode.API_1104, WPSCode.ASMESectionIX],
  [ConstructionCode.CFR193_ASMEB31_3]: [WPSCode.ASMESectionIX],
  [ConstructionCode.ASMEB31_8_NonCFR]: [WPSCode.API_1104, WPSCode.ASMESectionIX],
  [ConstructionCode.ASMEB31_4_NonCFR]: [WPSCode.ASMESectionIX, WPSCode.API_1104],
  [ConstructionCode.ASMEB31_3_NonCFR]: [WPSCode.ASMESectionIX],
  [ConstructionCode.Unknown]: [WPSCode.API_1104, WPSCode.ASMESectionIX, WPSCode.NotListed, WPSCode.Unknown],
  [ConstructionCode.NonCode]: [WPSCode.API_1104, WPSCode.ASMESectionIX, WPSCode.NotListed, WPSCode.Unknown],
  [ConstructionCode.NotListed]: [WPSCode.API_1104, WPSCode.ASMESectionIX, WPSCode.NotListed, WPSCode.Unknown],
};

function getWpsCodesMenu(data: ScopeOfWork): WPSCode[] {
  const constructionCode = data.constructionCode as ConstructionCode;
  if (!constructionCode) {
    return Object.values(WPSCode);
  }

  if (data.inServiceWelding) {
    return WPSCodes_InService[constructionCode] || Object.values(WPSCode);
  } else {
    return WPSCodes_OutOfService[constructionCode] || Object.values(WPSCode);
  }
}

function getContinuityTypeMenu(data: ScopeOfWork): ContinuityType[] {
  switch (data.constructionCode) {
    case ConstructionCode.CFR192_ASMEB31_8:
      return [ContinuityType.CFR192];
    case ConstructionCode.CFR195_ASMEB31_4:
      return [ContinuityType.CFR195];
    case ConstructionCode.CFR193_ASMEB31_3:
      return [ContinuityType.Basic, ContinuityType.BasicNDE];
    case ConstructionCode.ASMEB31_3_NonCFR:
      return [ContinuityType.Basic, ContinuityType.BasicNDE];
    default:
      return Object.values(ContinuityType);
  }
}

export const menus = {
  wpsCode: getWpsCodesMenu,
  continuity: getContinuityTypeMenu,
  service: Object.values(ServiceType),
  material: Object.values(MaterialType),
  constructionCode: Object.values(ConstructionCode),
  npsGroove: [
    "0.5 or less",
    "0.75",
    "1",
    "1.25",
    "1.5",
    "2",
    "2.5",
    "3",
    "3.5",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
    "14",
    "16",
    "18",
    "20",
    "24",
    "26",
    "28",
    "30",
    "36",
    "42",
    "48",
  ],
  thickestGroove: [
    "Unlimited",
    "0.109",
    "0.133",
    "0.154",
    "0.179",
    "0.188",
    "0.216",
    "0.218",
    "0.219",
    "0.237",
    "0.25",
    "0.258",
    "0.28",
    "0.281",
    "0.286",
    "0.3",
    "0.312",
    "0.322",
    "0.334",
    "0.337",
    "0.343",
    "0.344",
    "0.365",
    "0.375",
    "0.406",
    "0.412",
    "0.42",
    "0.429",
    "0.432",
    "0.436",
    "0.437",
    "0.438",
    "0.5",
    "0.515",
    "0.529",
    "0.552",
    "0.562",
    "0.594",
    "0.6",
    "0.618",
    "0.625",
    "0.635",
    "0.674",
    "0.688",
    "0.718",
    "0.719",
    "0.74",
    "0.75",
    "0.812",
    "0.844",
    "0.864",
    "0.875",
    "0.888",
    "0.906",
    "1",
    "1.181",
    "1.25",
  ],
};
